/**
 * fasteners.ts - Parametry mocowań i łączeń
 * PlexiSystem Engineering Module
 */

export interface FastenerSpec {
  torque_Nm: number;
  capacity_N: [number, number];
  preload_N?: number;
  thread_pitch_mm?: number;
  head_diameter_mm?: number;
}

export const FASTENERS = {
  // Momenty dokręcania dla tworzyw [Nm]
  torque_Nm: {
    M3: 0.5,
    M4: 1.2,
    M5: 2.5,
    M6: 4.0,
    M8: 7.0,
    M10: 12.0
  },
  
  // Nośność śrub w tworzywach [N]
  screw_capacity_N: {
    M3: [200, 350],
    M4: [400, 600],
    M5: [600, 900],
    M6: [800, 1400],
    M8: [1500, 2500],
    M10: [2500, 4000]
  },
  
  // Wytrzymałość na ścinanie [N]
  shear_capacity_N: {
    M3: [150, 250],
    M4: [300, 450],
    M5: [450, 700],
    M6: [600, 1000],
    M8: [1200, 1800],
    M10: [2000, 3000]
  },
  
  // Minimalne grubości materiału dla gwintów [mm]
  min_thickness_mm: {
    M3: 4,
    M4: 5,
    M5: 6,
    M6: 8,
    M8: 10,
    M10: 12
  },
  
  // Zawiasy - nośność [kg/para]
  hinges_capacity_kg: {
    small_40mm: [5, 8],      // PMMA/PETG
    medium_50mm: [10, 15],   // PMMA/PETG
    large_60mm: [15, 20],    // PMMA/PETG
    heavy_80mm: [20, 30],    // PC/wzmocnione
    piano_per_m: [25, 40]    // Zawias pianinowy na metr
  },
  
  // Kleje - wytrzymałość [MPa]
  adhesive_strength_MPa: {
    uv_bond: [15, 25],        // Klej UV
    solvent_weld: [20, 35],   // Klejenie rozpuszczalnikowe
    acrylic_cement: [25, 40], // Cement akrylowy
    epoxy: [15, 30],          // Żywica epoksydowa
    silicone: [2, 5],         // Silikon
    double_tape: [0.5, 1.5]   // Taśma dwustronna
  },
  
  // Współczynniki bezpieczeństwa dla różnych zastosowań
  safety_factors: {
    static: 2.0,              // Obciążenia statyczne
    dynamic: 3.0,             // Obciążenia dynamiczne
    cyclic: 4.0,              // Obciążenia cykliczne
    impact: 5.0               // Obciążenia udarowe
  },
  
  // Zalecane rozstawy śrub [mm]
  spacing_mm: {
    edge_min: 2.5,            // × średnica śruby od krawędzi
    between_min: 3.0,         // × średnica śruby między śrubami
    optimal: 5.0              // × średnica śruby optymalny rozstaw
  },
  
  // Wskazówki montażowe
  hints: [
    "Zawsze używaj podkładek o dużej średnicy na tworzywie",
    "Preferuj dystanse/tuleje - unikaj punktowego ścisku",
    "Klej UV tylko do estetycznych, statycznych połączeń",
    "Nie dokręcaj śrub zbyt mocno - ryzyko pęknięcia",
    "Wywierć otwory pilotujące o średnicy 0.8-0.9 × średnica śruby",
    "Dla połączeń rozłącznych użyj wkładek gwintowanych",
    "Temperatura montażu 18-25°C dla klejenia"
  ]
};

/**
 * Oblicza liczbę śrub potrzebnych dla danego obciążenia
 */
export function calculateScrewCount(
  load_N: number,
  screwSize: 'M3' | 'M4' | 'M5' | 'M6' | 'M8' | 'M10',
  loadType: 'static' | 'dynamic' | 'cyclic' | 'impact' = 'static',
  loadDirection: 'axial' | 'shear' = 'axial'
): {
  count: number;
  actualSF: number;
  screwCapacity: number;
} {
  const sf = FASTENERS.safety_factors[loadType];
  const capacity = loadDirection === 'axial' 
    ? FASTENERS.screw_capacity_N[screwSize]
    : FASTENERS.shear_capacity_N[screwSize];
  
  // Użyj dolnej granicy dla bezpieczeństwa
  const screwCapacity = capacity[0];
  const requiredCount = Math.ceil((load_N * sf) / screwCapacity);
  const actualSF = (requiredCount * screwCapacity) / load_N;
  
  return {
    count: requiredCount,
    actualSF: Math.round(actualSF * 10) / 10,
    screwCapacity
  };
}

/**
 * Oblicza wymaganą powierzchnię klejenia
 */
export function calculateBondArea(
  load_N: number,
  adhesiveType: 'uv_bond' | 'solvent_weld' | 'acrylic_cement' | 'epoxy' | 'silicone' | 'double_tape',
  loadType: 'static' | 'dynamic' = 'static'
): {
  area_mm2: number;
  area_cm2: number;
  width_for_10mm_joint: number;
} {
  const strength = FASTENERS.adhesive_strength_MPa[adhesiveType];
  const avgStrength = (strength[0] + strength[1]) / 2;
  const sf = loadType === 'dynamic' ? 3.0 : 2.0;
  
  const requiredArea_mm2 = (load_N * sf) / avgStrength;
  const area_cm2 = requiredArea_mm2 / 100;
  const width_for_10mm_joint = requiredArea_mm2 / 10;
  
  return {
    area_mm2: Math.ceil(requiredArea_mm2),
    area_cm2: Math.round(area_cm2 * 10) / 10,
    width_for_10mm_joint: Math.ceil(width_for_10mm_joint)
  };
}

/**
 * Dobiera zawiasy dla drzwi/klapy
 */
export function selectHinges(
  doorWeight_kg: number,
  doorHeight_mm: number,
  openingCycles?: number
): {
  type: string;
  count: number;
  size: string;
  capacity_kg: number;
} {
  // Dodaj współczynnik dla cykli otwarcia
  const cycleFactor = openingCycles && openingCycles > 10000 ? 1.5 : 1.0;
  const effectiveWeight = doorWeight_kg * cycleFactor;
  
  // Dobierz typ zawiasu
  if (doorHeight_mm > 1000 || effectiveWeight > 20) {
    // Zawias pianinowy
    const lengthNeeded_m = doorHeight_mm / 1000;
    const capacity = FASTENERS.hinges_capacity_kg.piano_per_m[0];
    
    return {
      type: 'piano',
      count: 1,
      size: `${Math.ceil(lengthNeeded_m * 1000)}mm`,
      capacity_kg: capacity * lengthNeeded_m
    };
  }
  
  // Standardowe zawiasy
  const hingeTypes = [
    { size: 'small_40mm', capacity: FASTENERS.hinges_capacity_kg.small_40mm },
    { size: 'medium_50mm', capacity: FASTENERS.hinges_capacity_kg.medium_50mm },
    { size: 'large_60mm', capacity: FASTENERS.hinges_capacity_kg.large_60mm },
    { size: 'heavy_80mm', capacity: FASTENERS.hinges_capacity_kg.heavy_80mm }
  ];
  
  for (const hinge of hingeTypes) {
    const pairCapacity = hinge.capacity[0];
    const pairsNeeded = Math.ceil(effectiveWeight / pairCapacity);
    
    if (pairsNeeded <= 3) { // Maksymalnie 3 pary
      return {
        type: 'standard',
        count: pairsNeeded * 2,
        size: hinge.size.replace('_', ' '),
        capacity_kg: pairCapacity * pairsNeeded
      };
    }
  }
  
  // Jeśli za ciężkie, użyj zawiasu pianinowego
  return {
    type: 'piano',
    count: 1,
    size: `${doorHeight_mm}mm`,
    capacity_kg: FASTENERS.hinges_capacity_kg.piano_per_m[0] * (doorHeight_mm / 1000)
  };
}

/**
 * Oblicza rozstaw śrub
 */
export function calculateScrewSpacing(
  screwDiameter_mm: number,
  edgeDistance_mm?: number
): {
  edge_min_mm: number;
  between_min_mm: number;
  optimal_mm: number;
} {
  const edge_min = screwDiameter_mm * FASTENERS.spacing_mm.edge_min;
  const between_min = screwDiameter_mm * FASTENERS.spacing_mm.between_min;
  const optimal = screwDiameter_mm * FASTENERS.spacing_mm.optimal;
  
  return {
    edge_min_mm: Math.ceil(edge_min),
    between_min_mm: Math.ceil(between_min),
    optimal_mm: Math.ceil(optimal)
  };
}

/**
 * Sprawdza możliwość gwintowania w materiale
 */
export function checkThreadingPossibility(
  materialThickness_mm: number,
  screwSize: 'M3' | 'M4' | 'M5' | 'M6' | 'M8' | 'M10'
): {
  possible: boolean;
  recommendation?: string;
} {
  const minThickness = FASTENERS.min_thickness_mm[screwSize];
  
  if (materialThickness_mm >= minThickness) {
    return {
      possible: true,
      recommendation: 'Gwintowanie możliwe'
    };
  } else if (materialThickness_mm >= minThickness * 0.7) {
    return {
      possible: true,
      recommendation: 'Użyj wkładki gwintowanej lub nakrętki'
    };
  } else {
    return {
      possible: false,
      recommendation: `Materiał za cienki (min. ${minThickness}mm dla ${screwSize})`
    };
  }
}
